import { useState, useEffect } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNotification } from "hooks/useNotification";
import { FinancialStatementService } from "services/financialStatement";
import { ExaminerRewardsService } from "services/examinerRewards";

type Props = {
  isOpen: boolean;
  statementId: number;
  onClose: (refetch?: boolean) => void;
};

export default function UpdateFinancialStatementModal({ isOpen, statementId, onClose }: Props) {
  const { t } = useTranslation(["FinancialStatement", "Common"]);
  const { showNotification } = useNotification();

  const [serialNumber, setSerialNumber] = useState("");
  const [examinerRewardIds, setExaminerRewardIds] = useState<number[]>([]);
  const [researchPaymentIds, setResearchPaymentIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [pendingExaminerRewards, setPendingExaminerRewards] = useState<any[]>([]);
  const [pendingResearchPayments, setPendingResearchPayments] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, statementId]);

  const fetchData = async () => {
    try {
      setFetchingData(true);

      // Fetch current statement
      const statementResponse = await FinancialStatementService.getFinancialStatementById(statementId);
      if (!statementResponse.hasError && statementResponse.data) {
        setSerialNumber(statementResponse.data.serialNumber);
        setExaminerRewardIds(statementResponse.data.examinerRewards.map((r) => r.id));
        setResearchPaymentIds(statementResponse.data.researchPayments.map((p) => p.id));
      }

      // Fetch pending examiner rewards
      const examinerRewardsResponse = await ExaminerRewardsService.getAllExaminersRewards({
        pageIndex: 1,
        pageSize: 1000,
        search: "",
        sort: [],
        filter: [{ field: "status", value: "Pending", operator: "contains" }],
      });
      setPendingExaminerRewards(examinerRewardsResponse.data.result || []);

      // TODO: Fetch pending research payments when service is available
      setPendingResearchPayments([]);
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setFetchingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!serialNumber.trim()) {
      showNotification({
        type: "error",
        description: t("serialNumberRequired"),
      });
      return;
    }

    if (examinerRewardIds.length === 0 && researchPaymentIds.length === 0) {
      showNotification({
        type: "error",
        description: t("noItemsSelected"),
      });
      return;
    }

    try {
      setLoading(true);
      const response = await FinancialStatementService.updateFinancialStatement(statementId, {
        serialNumber: serialNumber.trim(),
        examinerRewardIds,
        researchPaymentIds,
      });

      if (!response.hasError) {
        showNotification({
          type: "success",
          description: t("financialStatementUpdatedSuccessfully"),
        });
        onClose(true);
      } else {
        showNotification({
          type: "error",
          description: response.description || t("Common:internalServerError"),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExaminerRewardToggle = (id: number) => {
    setExaminerRewardIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleResearchPaymentToggle = (id: number) => {
    setResearchPaymentIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <Modal show={isOpen} onHide={() => onClose()} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="w-100">{t("updateFinancialStatement")}</Modal.Title>
      </Modal.Header>
      {fetchingData ? (
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("Common:loading")}</span>
          </Spinner>
        </Modal.Body>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t("serialNumber")}</Form.Label>
              <Form.Control
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder={t("serialNumber")}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("pendingExaminerRewards")}</Form.Label>
              <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "4px", padding: "10px" }}>
                {pendingExaminerRewards.length === 0 ? (
                  <p className="text-muted mb-0">{t("Common:noDataFound")}</p>
                ) : (
                  pendingExaminerRewards.map((reward) => (
                    <Form.Check
                      key={reward.id}
                      type="checkbox"
                      id={`examiner-reward-${reward.id}`}
                      label={`${reward.examiner?.arabicName || reward.examiner?.englishName} - ${reward.value} ${t("riyal")}`}
                      checked={examinerRewardIds.includes(reward.id)}
                      onChange={() => handleExaminerRewardToggle(reward.id)}
                    />
                  ))
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("pendingResearchPayments")}</Form.Label>
              <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "4px", padding: "10px" }}>
                {pendingResearchPayments.length === 0 ? (
                  <p className="text-muted mb-0">{t("Common:noDataFound")}</p>
                ) : (
                  pendingResearchPayments.map((payment) => (
                    <Form.Check
                      key={payment.id}
                      type="checkbox"
                      id={`research-payment-${payment.id}`}
                      label={`${payment.programOrderId} - ${payment.amount} ${t("riyal")}`}
                      checked={researchPaymentIds.includes(payment.id)}
                      onChange={() => handleResearchPaymentToggle(payment.id)}
                    />
                  ))
                )}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-secondary" onClick={() => onClose()} disabled={loading}>
              <i className="icon-close mx-2"></i>
              {t("cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
                  {t("Common:loading")}
                </>
              ) : (
                <>
                  <i className="icon-check mx-2"></i>
                  {t("save")}
                </>
              )}
            </button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}
